/**
 * The index entries — the routing fields of each document in the folder:
 * what kind it is, which report types it applies to, and the description
 * the agents read it by. The files stay as published; a change of file is a
 * new folder publish. The author's description is kept exactly; a blank
 * one is written by the model at the next index.
 */

import { Anchor, Select, Stack, Table, Text, Textarea, TextInput } from '@mantine/core';

import {
  PLAYBOOK_DOCUMENT_KINDS,
  type PlaybookDocument,
  type PlaybookEntry,
} from '@/types/playbook';
import { listFrom, words } from './format';

interface EntriesEditorProps {
  documents: PlaybookDocument[];
  entries: PlaybookEntry[];
  onChange: (entries: PlaybookEntry[]) => void;
  readOnly?: boolean;
  /** Open a document's file. */
  onOpenFile?: (document: PlaybookDocument) => void;
}

export function EntriesEditor({
  documents,
  entries,
  onChange,
  readOnly = false,
  onOpenFile,
}: Readonly<EntriesEditorProps>) {
  const byKey = new Map(documents.map((d) => [d.key, d]));
  const update = (at: number, patch: Partial<PlaybookEntry>) =>
    onChange(entries.map((entry, i) => (i === at ? { ...entry, ...patch } : entry)));
  if (entries.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No documents in this version.
      </Text>
    );
  }
  return (
    <Table verticalSpacing={6} fz="sm" data-testid="entries-editor">
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={200}>Document</Table.Th>
          <Table.Th w={150}>Kind</Table.Th>
          <Table.Th w={160}>Applies to</Table.Th>
          <Table.Th>Description</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {entries.map((entry, at) => {
          const document = byKey.get(entry.key);
          return (
            <Table.Tr key={entry.key} data-testid={`entry-${entry.key}`}>
              <Table.Td>
                <Stack gap={0}>
                  <Text size="sm" fw={500}>
                    {entry.key}
                  </Text>
                  {document ? (
                    onOpenFile && document.fileId ? (
                      <Anchor
                        component="button"
                        type="button"
                        size="xs"
                        onClick={() => onOpenFile(document)}
                        style={{ textAlign: 'left' }}
                        data-testid={`entry-file-${entry.key}`}
                      >
                        {document.path}
                      </Anchor>
                    ) : (
                      <Text size="xs" c="dimmed">
                        {document.path}
                      </Text>
                    )
                  ) : null}
                  {document?.size ? (
                    <Text size="xs" c="dimmed">
                      about {document.size.toLocaleString()} tokens
                    </Text>
                  ) : null}
                </Stack>
              </Table.Td>
              <Table.Td>
                <Select
                  size="xs"
                  data={PLAYBOOK_DOCUMENT_KINDS.map((kind) => ({
                    value: kind,
                    label: words(kind),
                  }))}
                  value={entry.kind}
                  onChange={(value) => value && update(at, { kind: value })}
                  readOnly={readOnly}
                  allowDeselect={false}
                  aria-label={`Kind of ${entry.key}`}
                  data-testid={`entry-kind-${entry.key}`}
                />
              </Table.Td>
              <Table.Td>
                <TextInput
                  size="xs"
                  placeholder="all"
                  value={entry.appliesTo === 'all' ? '' : entry.appliesTo.join(', ')}
                  onChange={(e) => {
                    const codes = listFrom(e.currentTarget.value);
                    update(at, { appliesTo: codes.length === 0 ? 'all' : codes });
                  }}
                  readOnly={readOnly}
                  aria-label={`Report types ${entry.key} applies to`}
                  data-testid={`entry-applies-${entry.key}`}
                />
              </Table.Td>
              <Table.Td>
                <Textarea
                  size="xs"
                  autosize
                  minRows={1}
                  placeholder="Left blank, the model writes one at the next index"
                  value={entry.description}
                  onChange={(e) => update(at, { description: e.currentTarget.value })}
                  readOnly={readOnly}
                  aria-label={`Description of ${entry.key}`}
                  data-testid={`entry-description-${entry.key}`}
                />
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
